import { GRAY_200, GRAY_300, GRAY_600, GREEN_100, GREEN_300, GREEN_700, YELLOW_100, YELLOW_300, YELLOW_700 } from 'constants/styling/theme'

import { FC, useEffect } from 'react'
import { MessageAction } from 'models/conversations'
import Typography from '@mui/material/Typography'
import { logger } from 'utils/logging'

type Status = NonNullable<MessageAction['status']>

/** Outlined + tinted pill styling per status tone — distinct from the neutral accent used for selection. */
const STATUS_TONE_STYLES: Record<Status['tone'], { backgroundColor: string; borderColor: string; color: string }> = {
  success: { backgroundColor: GREEN_100, borderColor: GREEN_300, color: GREEN_700 },
  pending: { backgroundColor: YELLOW_100, borderColor: YELLOW_300, color: YELLOW_700 },
  neutral: { backgroundColor: GRAY_200, borderColor: GRAY_300, color: GRAY_600 },
}

const reportedUnknownTones = new Set<string>()

interface Props {
  status: Status
  compact?: boolean
}

export const StatusPill: FC<Props> = ({ status, compact = false }) => {
  // The tone is backend-supplied: an unknown one must degrade, not throw during render.
  const isKnownTone = Object.hasOwn(STATUS_TONE_STYLES, status.tone)
  const style = isKnownTone ? STATUS_TONE_STYLES[status.tone] : STATUS_TONE_STYLES.neutral

  useEffect(() => {
    const tone = String(status.tone)
    if (isKnownTone || reportedUnknownTones.has(tone)) return
    reportedUnknownTones.add(tone)
    logger.warn('StatusPill: unknown status tone, shown as neutral', undefined, {
      context: { component: 'StatusPill', action: 'statusTone', metadata: { tone } },
    })
  }, [isKnownTone, status.tone])

  return (
    <Typography
      variant="text-xs"
      fontWeight={600}
      sx={{
        flexShrink: 0,
        padding: compact ? '0.2rem 0.8rem' : '0.3rem 1.1rem',
        borderRadius: '999px',
        border: `1px solid ${style.borderColor}`,
        backgroundColor: style.backgroundColor,
        color: style.color,
      }}
    >
      {status.label}
    </Typography>
  )
}
